import { useState } from "react";
import { FileArrowUp, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { API, fmtErr, inr, isUpgradeError, toastUpgrade } from "../lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";

const CONF_CLS = {
  exact: "border-[#008A52]/30 bg-[#008A52]/10 text-[#008A52]",
  close: "border-[#B7791F]/30 bg-[#B7791F]/10 text-[#B7791F]",
  weak: "border-border text-muted-foreground",
};

export function StatementUpload({ accounts, onDone }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [accountId, setAccountId] = useState("");
  const [rows, setRows] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);

  const reset = () => {
    setFile(null);
    setRows(null);
    setAccountId("");
  };

  const parse = async () => {
    if (!file) return toast.error("Choose a CSV or Excel statement file");
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await API.post("/banking/statements/parse", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const parsed = r.data.rows.map((row) => ({
        ...row,
        selected: !row.duplicate_transaction,
        chosen: row.match ? row.match.id : "",
      }));
      if (parsed.length === 0) return toast.error("No transactions detected in file");
      setRows(parsed);
      toast.success(`Parsed ${parsed.length} transactions`);
    } catch (e) {
      if (isUpgradeError(e)) {
        toastUpgrade(e);
      } else {
        toast.error(fmtErr(e));
      }
    } finally {
      setParsing(false);
    }
  };

  const setRow = (i, patch) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const selectAllRows = (selected) => setRows((rs) => rs.map((row) => ({ ...row, selected })));

  const doImport = async () => {
    if (!accountId) return toast.error("Select the bank account for this statement");
    const chosen = rows.filter((r) => r.selected);
    if (chosen.length === 0) return toast.error("Select at least one row");
    setImporting(true);
    try {
      const r = await API.post("/banking/statements/import", {
        account_id: accountId,
        rows: chosen.map((r) => ({
          date: r.date,
          description: r.description,
          type: r.type,
          amount: r.amount,
          transaction_no: r.transaction_no || "",
          match_id: r.chosen || null,
        })),
      });
      toast.success(`Imported ${r.data.imported} transactions, matched ${r.data.matched} payments, skipped ${r.data.duplicates || 0} duplicates`);
      setOpen(false);
      reset();
      onDone?.();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setImporting(false);
    }
  };

  const matchLabel = (c) => `${c.number} · ${c.party_name} · due ${inr(c.due)}`;
  const matchedCount = rows ? rows.filter((r) => r.selected && r.chosen).length : 0;
  const duplicateCount = rows ? rows.filter((r) => r.duplicate_transaction).length : 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="upload-statement-button">
          <FileArrowUp size={16} className="mr-1" /> Upload Statement
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] max-w-5xl overflow-y-auto" data-testid="statement-upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Import Bank Statement</DialogTitle>
          <DialogDescription className="sr-only">Upload a CSV or Excel bank statement and match transactions to unpaid invoices</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-2">
            <Label>Bank Account</Label>
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger data-testid="statement-account-select">
                <SelectValue placeholder="Select account" />
              </SelectTrigger>
              <SelectContent>
                {accounts.map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label>Statement File (CSV / XLSX / PDF)</Label>
            <div className="flex gap-2">
              <input
                type="file"
                accept=".csv,.xlsx,.xls,.pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm file:mr-3 file:rounded-sm file:border-0 file:bg-muted file:px-3 file:text-xs file:font-medium"
                data-testid="statement-file-input"
              />
              <Button onClick={parse} disabled={parsing} variant="secondary" data-testid="statement-parse-button">
                <UploadSimple size={15} className="mr-1" /> {parsing ? "Uploading…" : "Upload"}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Date, description, and Debit/Credit type are read from the statement. Type cannot be changed here. Works with CSV, Excel, and PDF statements.
            </p>
          </div>
        </div>

        {rows && (
          <>
            <div className="flex items-center justify-between rounded-md border bg-muted/40 px-4 py-2 text-sm" data-testid="statement-summary">
              <span>{rows.filter((r) => r.selected).length} of {rows.length} rows selected{duplicateCount ? ` · ${duplicateCount} duplicate transaction${duplicateCount === 1 ? "" : "s"} highlighted and unselected` : ""}</span>
              <div className="flex items-center gap-3">
                <Button variant="ghost" size="sm" onClick={() => selectAllRows(rows.some((r) => !r.selected))} data-testid="statement-select-all">
                  {rows.every((r) => r.selected) ? "Select none" : "Select all"}
                </Button>
                <span className="text-muted-foreground">{matchedCount} matched to open invoices</span>
              </div>
            </div>
            <div className="overflow-x-auto rounded-md border">
              <Table data-testid="statement-rows-table" className="min-w-[640px]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10"><Checkbox checked={rows.every((r) => r.selected)} onCheckedChange={(v) => selectAllRows(!!v)} aria-label="Select all statement transactions" /></TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Narration</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Debit</TableHead>
                    <TableHead className="text-right">Credit</TableHead>
                    <TableHead className="w-[320px]">Best Matches — open {rows.some((r) => r.type === "deposit") ? "invoices / bills" : "bills"} (top 5)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r, i) => (
                    <TableRow key={i} className={r.duplicate_transaction ? "border-[#E02424]/40 bg-[#E02424]/10" : r.selected ? "" : "opacity-50"} data-testid={`statement-row-${i}`}>
                      <TableCell>
                        <Checkbox checked={r.selected} onCheckedChange={(v) => setRow(i, { selected: !!v })} data-testid={`statement-row-select-${i}`} />
                      </TableCell>
                      <TableCell className="whitespace-nowrap">{r.date}</TableCell>
                      <TableCell className="max-w-[240px] truncate" title={r.description}>
                        <span>{r.description || "—"}</span>
                        {r.duplicate_transaction && <Badge variant="outline" className="ml-2 border-[#E02424]/30 bg-[#E02424]/10 text-[#E02424]" data-testid={`statement-duplicate-badge-${i}`}>Duplicate Transaction</Badge>}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={r.type === "withdrawal"
                            ? "border-[#E02424]/30 bg-[#E02424]/10 text-[#E02424]"
                            : "border-[#008A52]/30 bg-[#008A52]/10 text-[#008A52]"}
                          data-testid={`statement-row-type-${i}`}
                        >
                          {r.type === "withdrawal" ? "Debit" : "Credit"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium tabular-nums text-[#E02424]">{r.type === "withdrawal" ? inr(r.amount) : "—"}</TableCell>
                      <TableCell className="text-right font-medium tabular-nums text-[#008A52]">{r.type === "deposit" ? inr(r.amount) : "—"}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Select value={r.chosen || "none"} onValueChange={(v) => setRow(i, { chosen: v === "none" ? "" : v })}>
                            <SelectTrigger className="h-8 text-xs" data-testid={`statement-match-select-${i}`}>
                              <SelectValue placeholder="No match" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">No match — import as-is</SelectItem>
                              {r.candidates.map((c) => (
                                <SelectItem key={c.id} value={c.id}>{matchLabel(c)}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {r.match && r.chosen === r.match.id && (
                            <Badge variant="outline" className={CONF_CLS[r.match.confidence]} data-testid={`statement-match-badge-${i}`}>
                              {r.match.confidence}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setRows(null)} data-testid="statement-back-button">Back</Button>
              <Button onClick={doImport} disabled={importing} data-testid="statement-import-button">
                {importing ? "Importing…" : `Import ${rows.filter((r) => r.selected).length} Transactions`}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
